import { useState, useEffect, useCallback, useRef } from "react";
import { DealerLayout } from "@/components/shared/DealerLayout";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  getTrainingMaterials,
  completeTraining,
  getTrainingCompletions,
  recordVideoComplete,
  submitTrainingQuiz,
  getTrainingProgress,
  getDealerRepairStatus,
} from "@/lib/api";
import { toast } from "sonner";
import { motion } from "framer-motion";
import {
  GraduationCap, Video, FileText, Loader2, CheckCircle, Clock,
  History, ChevronLeft, ChevronRight, HelpCircle, Play, Award,
  Lock, ExternalLink, X, ArrowLeft,
} from "lucide-react";

const typeIcons = { video: Video, document: FileText, link: ExternalLink };

function SlideshowViewer({ material, onComplete, onBack }) {
  const [currentPage, setCurrentPage] = useState(0);
  const [videoWatched, setVideoWatched] = useState(false);
  const [videoEnded, setVideoEnded] = useState(false);
  const [quizMode, setQuizMode] = useState(false);
  const [answers, setAnswers] = useState([]);
  const [quizResult, setQuizResult] = useState(null);
  const [progress, setProgress] = useState(null);
  const [submittingQuiz, setSubmittingQuiz] = useState(false);
  const [completing, setCompleting] = useState(false);
  const [loadingProgress, setLoadingProgress] = useState(true);
  const videoRef = useRef(null);

  const pages = material.pages || [];
  const quiz = material.quiz || {};
  const questions = quiz.questions || [];
  const hasVideo = material.type === "video" && material.url;
  const hasQuiz = questions.length > 0;
  const hasPages = pages.length > 0;

  const fetchProgress = useCallback(async () => {
    try {
      const prog = await getTrainingProgress(material.id);
      setProgress(prog);
      setVideoWatched(prog.video_completed);
    } catch (err) { console.error(err); }
    finally { setLoadingProgress(false); }
  }, [material.id]);

  useEffect(() => { fetchProgress(); }, [fetchProgress]);

  const handleVideoEnd = async () => {
    setVideoEnded(true);
    if (!videoWatched) {
      try {
        await recordVideoComplete(material.id);
        setVideoWatched(true);
        toast.success("Video completed!");
        fetchProgress();
      } catch (err) { toast.error("Failed to record video completion"); }
    }
  };

  const handleQuizSubmit = async () => {
    if (answers.length !== questions.length || answers.some((a) => a === undefined)) {
      toast.error("Please answer all questions");
      return;
    }
    setSubmittingQuiz(true);
    try {
      const result = await submitTrainingQuiz(material.id, answers);
      setQuizResult(result);
      fetchProgress();
      if (result.passed) {
        toast.success(`Quiz passed with ${result.score}%!`);
      } else {
        toast.error(`Score: ${result.score}%. Need 80% to pass. Try again!`);
      }
    } catch (err) { toast.error(err?.response?.data?.detail || "Quiz submission failed"); }
    finally { setSubmittingQuiz(false); }
  };

  const handleMarkComplete = async () => {
    setCompleting(true);
    try {
      await completeTraining(material.id);
      toast.success("Training completed!");
      onComplete();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Cannot complete training yet");
    } finally { setCompleting(false); }
  };

  const canComplete = () => {
    if (!progress) return false;
    if (hasVideo && !progress.video_completed) return false;
    if (hasQuiz && !progress.quiz_passed) return false;
    return true;
  };

  const retakeQuiz = () => {
    setQuizResult(null);
    setAnswers([]);
  };

  if (loadingProgress) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="w-8 h-8 animate-spin text-teal" />
      </div>
    );
  }

  const page = pages[currentPage];

  return (
    <div data-testid="training-viewer">
      <div className="flex items-center gap-3 mb-6">
        <Button variant="ghost" size="sm" onClick={onBack} data-testid="training-back-btn">
          <ArrowLeft className="w-4 h-4 mr-1" /> Back
        </Button>
        <div className="flex-1">
          <h2 className="font-heading font-bold text-xl text-navy">{material.title}</h2>
          <p className="text-sm text-slate-500">{material.category}</p>
        </div>
      </div>

      {/* Progress bar */}
      <div className="flex items-center gap-3 mb-6 p-3 bg-slate-50 rounded-lg" data-testid="training-progress-bar">
        {hasVideo && (
          <div className={`flex items-center gap-1.5 text-xs font-medium px-2.5 py-1 rounded-full ${progress?.video_completed ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"}`}>
            <Video className="w-3 h-3" />
            Video {progress?.video_completed ? "Done" : "Required"}
          </div>
        )}
        {hasQuiz && (
          <div className={`flex items-center gap-1.5 text-xs font-medium px-2.5 py-1 rounded-full ${progress?.quiz_passed ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"}`}>
            <HelpCircle className="w-3 h-3" />
            Quiz {progress?.quiz_passed ? `Passed (${progress.best_quiz_score}%)` : "Required (80%+)"}
          </div>
        )}
        {hasPages && (
          <div className="flex items-center gap-1.5 text-xs font-medium px-2.5 py-1 rounded-full bg-slate-200 text-slate-700">
            <FileText className="w-3 h-3" />
            {pages.length} Pages
          </div>
        )}
      </div>

      {/* Video section */}
      {hasVideo && !quizMode && (
        <Card className="border-slate-200 mb-6" data-testid="training-video-section">
          <CardContent className="p-4">
            <div className="aspect-video bg-black rounded-lg overflow-hidden">
              <video
                ref={videoRef}
                src={material.url}
                controls
                className="w-full h-full"
                onEnded={handleVideoEnd}
                data-testid="training-video"
              />
            </div>
            <div className="mt-3 flex items-center justify-between">
              <p className="text-sm text-slate-500">
                {videoWatched ? (
                  <span className="text-emerald-600 flex items-center gap-1"><CheckCircle className="w-4 h-4" /> Video completed</span>
                ) : (
                  <span className="text-amber-600 flex items-center gap-1"><Play className="w-4 h-4" /> Watch the entire video to continue</span>
                )}
              </p>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Slideshow pages */}
      {hasPages && !quizMode && (
        <Card className="border-slate-200 mb-6" data-testid="training-slideshow">
          <CardContent className="p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-heading font-semibold text-navy">Page {currentPage + 1} of {pages.length}</h3>
              <div className="flex gap-1">
                {pages.map((_, i) => (
                  <button
                    key={i}
                    onClick={() => setCurrentPage(i)}
                    className={`w-2.5 h-2.5 rounded-full transition-colors ${i === currentPage ? "bg-teal" : "bg-slate-200"}`}
                    data-testid={`page-dot-${i}`}
                  />
                ))}
              </div>
            </div>
            {page && (
              <div>
                {page.image_url && (
                  <img src={page.image_url} alt="" className="w-full max-h-80 object-contain rounded-lg mb-4 bg-slate-50" />
                )}
                {page.video_url && (
                  <div className="aspect-video bg-black rounded-lg overflow-hidden mb-4">
                    <video src={page.video_url} controls className="w-full h-full" />
                  </div>
                )}
                <div className="prose prose-sm max-w-none text-slate-700 whitespace-pre-wrap">{page.content}</div>
              </div>
            )}
            <div className="flex items-center justify-between mt-6 pt-4 border-t">
              <Button variant="outline" size="sm" disabled={currentPage === 0} onClick={() => setCurrentPage(currentPage - 1)} data-testid="slide-prev">
                <ChevronLeft className="w-4 h-4 mr-1" /> Previous
              </Button>
              <Button variant="outline" size="sm" disabled={currentPage === pages.length - 1} onClick={() => setCurrentPage(currentPage + 1)} data-testid="slide-next">
                Next <ChevronRight className="w-4 h-4 ml-1" />
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Description / content for non-page trainings */}
      {!hasPages && material.content && !quizMode && (
        <Card className="border-slate-200 mb-6">
          <CardContent className="p-6">
            <div className="prose prose-sm max-w-none text-slate-700 whitespace-pre-wrap">{material.content}</div>
          </CardContent>
        </Card>
      )}

      {/* Quiz Section */}
      {hasQuiz && !quizMode && !progress?.quiz_passed && (
        <Card className="border-slate-200 mb-6">
          <CardContent className="p-6 text-center">
            <HelpCircle className="w-10 h-10 text-indigo-400 mx-auto mb-3" />
            <h3 className="font-heading font-semibold text-navy mb-2">Quiz Required</h3>
            <p className="text-sm text-slate-500 mb-4">You must pass the quiz with 80% or higher to complete this training.</p>
            {progress?.latest_quiz_score !== null && progress?.latest_quiz_score !== undefined && (
              <p className="text-sm text-amber-600 mb-3">Last attempt: {progress.latest_quiz_score}%</p>
            )}
            <Button onClick={() => { setQuizMode(true); setAnswers([]); setQuizResult(null); }} className="bg-indigo-500 hover:bg-indigo-600 text-white" data-testid="start-quiz-btn">
              <HelpCircle className="w-4 h-4 mr-2" /> {progress?.latest_quiz_score !== null && progress?.latest_quiz_score !== undefined ? "Retake Quiz" : "Start Quiz"}
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Quiz Mode */}
      {quizMode && !quizResult && (
        <Card className="border-slate-200 mb-6" data-testid="quiz-section">
          <CardContent className="p-6">
            <div className="flex items-center justify-between mb-6">
              <h3 className="font-heading font-semibold text-navy">Quiz - {questions.length} Questions</h3>
              <Button variant="ghost" size="sm" onClick={() => setQuizMode(false)}>
                <X className="w-4 h-4 mr-1" /> Cancel
              </Button>
            </div>
            <div className="space-y-6">
              {questions.map((q, qi) => (
                <div key={qi} className="p-4 bg-slate-50 rounded-lg" data-testid={`quiz-question-${qi}`}>
                  <p className="font-medium text-navy mb-3">{qi + 1}. {q.question}</p>
                  <div className="space-y-2">
                    {q.options.map((opt, oi) => (
                      <label
                        key={oi}
                        className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${answers[qi] === oi ? "border-teal bg-teal/5" : "border-slate-200 hover:bg-slate-100"}`}
                        data-testid={`quiz-option-${qi}-${oi}`}
                      >
                        <input
                          type="radio"
                          name={`q-${qi}`}
                          checked={answers[qi] === oi}
                          onChange={() => {
                            const next = [...answers];
                            next[qi] = oi;
                            setAnswers(next);
                          }}
                          className="accent-teal"
                        />
                        <span className="text-sm text-slate-700">{opt}</span>
                      </label>
                    ))}
                  </div>
                </div>
              ))}
            </div>
            <div className="mt-6 pt-4 border-t flex justify-end">
              <Button onClick={handleQuizSubmit} disabled={submittingQuiz} className="bg-teal hover:bg-teal/90 text-white" data-testid="submit-quiz-btn">
                {submittingQuiz ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Award className="w-4 h-4 mr-2" />}
                Submit Quiz
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Quiz Result */}
      {quizResult && (
        <Card className={`border-2 mb-6 ${quizResult.passed ? "border-emerald-300 bg-emerald-50/50" : "border-red-300 bg-red-50/50"}`} data-testid="quiz-result">
          <CardContent className="p-6 text-center">
            {quizResult.passed ? (
              <CheckCircle className="w-12 h-12 text-emerald-500 mx-auto mb-3" />
            ) : (
              <X className="w-12 h-12 text-red-500 mx-auto mb-3" />
            )}
            <h3 className="font-heading font-bold text-xl mb-2">
              {quizResult.passed ? "Quiz Passed!" : "Quiz Not Passed"}
            </h3>
            <p className="text-2xl font-bold mb-2">{quizResult.score}%</p>
            <p className="text-sm text-slate-500 mb-4">
              {quizResult.correct_count} of {quizResult.total_count} correct
            </p>
            {!quizResult.passed && (
              <Button onClick={retakeQuiz} className="bg-indigo-500 hover:bg-indigo-600 text-white" data-testid="retake-quiz-btn">
                <HelpCircle className="w-4 h-4 mr-2" /> Retake Quiz
              </Button>
            )}
            {quizResult.passed && (
              <Button onClick={() => { setQuizMode(false); setQuizResult(null); }} className="bg-teal hover:bg-teal/90 text-white">
                Continue
              </Button>
            )}
          </CardContent>
        </Card>
      )}

      {/* Complete button */}
      {!quizMode && (
        <div className="flex justify-end">
          <Button
            onClick={handleMarkComplete}
            disabled={!canComplete() || completing}
            className={`px-6 ${canComplete() ? "bg-emerald-500 hover:bg-emerald-600 text-white" : "bg-slate-200 text-slate-400 cursor-not-allowed"}`}
            data-testid="mark-complete-btn"
          >
            {completing ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <CheckCircle className="w-4 h-4 mr-2" />}
            Mark Training Complete
          </Button>
        </div>
      )}
    </div>
  );
}

export default function DealerTraining() {
  const [materials, setMaterials] = useState([]);
  const [completions, setCompletions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("active");
  const [selectedMaterial, setSelectedMaterial] = useState(null);
  const [repairStatus, setRepairStatus] = useState("none");

  const fetchAll = useCallback(async () => {
    try {
      const [mats, comps, repairData] = await Promise.all([
        getTrainingMaterials(),
        getTrainingCompletions(),
        getDealerRepairStatus(),
      ]);
      setMaterials(mats);
      setCompletions(comps);
      setRepairStatus(repairData.best_repair_status);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const completedIds = new Set(completions.map((c) => c.material_id));

  const activeMaterials = materials.filter((m) => {
    if (completedIds.has(m.id)) return false;
    if (m.is_repair_content && repairStatus !== "training" && repairStatus !== "approved") return false;
    return true;
  });

  const completedMaterials = completions.map((c) => {
    const mat = materials.find((m) => m.id === c.material_id);
    return { ...c, ...(mat || {}) };
  });

  if (loading) {
    return (
      <DealerLayout title="Training Center">
        <div className="flex items-center justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-teal" /></div>
      </DealerLayout>
    );
  }

  if (selectedMaterial) {
    return (
      <DealerLayout title="Training Center">
        <div className="max-w-4xl mx-auto" data-testid="dealer-training-page">
          <SlideshowViewer
            material={selectedMaterial}
            onBack={() => setSelectedMaterial(null)}
            onComplete={() => { setSelectedMaterial(null); fetchAll(); }}
          />
        </div>
      </DealerLayout>
    );
  }

  return (
    <DealerLayout title="Training Center">
      <div className="max-w-4xl mx-auto" data-testid="dealer-training-page">
        <div className="mb-6">
          <h1 className="font-heading font-bold text-2xl text-navy">Training Center</h1>
          <p className="text-slate-500 font-body text-sm mt-1">Complete training modules to enhance your skills</p>
        </div>

        <div className="flex gap-2 mb-6">
          <button
            onClick={() => setActiveTab("active")}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${activeTab === "active" ? "bg-teal text-white" : "bg-white text-slate-600 border border-slate-200 hover:bg-slate-50"}`}
            data-testid="tab-active-training"
          >
            <GraduationCap className="w-4 h-4 inline mr-1.5" />
            Active ({activeMaterials.length})
          </button>
          <button
            onClick={() => setActiveTab("history")}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${activeTab === "history" ? "bg-teal text-white" : "bg-white text-slate-600 border border-slate-200 hover:bg-slate-50"}`}
            data-testid="tab-training-history"
          >
            <History className="w-4 h-4 inline mr-1.5" />
            History ({completedMaterials.length})
          </button>
        </div>

        {activeTab === "active" && (
          <div className="space-y-3" data-testid="active-training-list">
            {activeMaterials.length === 0 ? (
              <Card className="border-slate-200">
                <CardContent className="p-12 text-center">
                  <CheckCircle className="w-12 h-12 text-emerald-300 mx-auto mb-4" />
                  <p className="text-slate-500 font-body text-lg">All trainings completed!</p>
                  <p className="text-slate-400 font-body text-sm mt-1">You've finished all available training materials</p>
                </CardContent>
              </Card>
            ) : (
              activeMaterials.map((mat, i) => {
                const TypeIcon = typeIcons[mat.type] || FileText;
                const hasQuiz = mat.quiz?.questions?.length > 0;
                const hasPages = mat.pages?.length > 0;
                return (
                  <motion.div key={mat.id} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.04 }}>
                    <Card
                      className="border-slate-200 hover:border-teal/30 transition-colors cursor-pointer"
                      onClick={() => setSelectedMaterial(mat)}
                      data-testid={`training-card-${mat.id}`}
                    >
                      <CardContent className="p-5 flex items-start gap-4">
                        <div className="w-10 h-10 bg-indigo-50 rounded-lg flex items-center justify-center flex-shrink-0">
                          <TypeIcon className="w-5 h-5 text-indigo-500" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <h3 className="font-heading font-semibold text-navy text-sm">{mat.title}</h3>
                          {mat.description && <p className="text-xs text-slate-500 mt-1 line-clamp-2">{mat.description}</p>}
                          <div className="flex items-center gap-2 mt-2 flex-wrap">
                            <span className="text-xs bg-slate-100 text-slate-600 px-2 py-0.5 rounded">{mat.category || "General"}</span>
                            {mat.is_repair_content && <span className="text-xs bg-amber-100 text-amber-700 px-2 py-0.5 rounded">Repair</span>}
                            {hasPages && <span className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded">{mat.pages.length} pages</span>}
                            {hasQuiz && <span className="text-xs bg-purple-100 text-purple-700 px-2 py-0.5 rounded">Quiz</span>}
                            {mat.type === "video" && <span className="text-xs bg-rose-100 text-rose-700 px-2 py-0.5 rounded">Video</span>}
                          </div>
                        </div>
                        <ChevronRight className="w-5 h-5 text-slate-300 flex-shrink-0 mt-1" />
                      </CardContent>
                    </Card>
                  </motion.div>
                );
              })
            )}
          </div>
        )}

        {activeTab === "history" && (
          <div className="space-y-3" data-testid="training-history-list">
            {completedMaterials.length === 0 ? (
              <Card className="border-slate-200">
                <CardContent className="p-12 text-center">
                  <History className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                  <p className="text-slate-500 font-body text-lg">No completed trainings yet</p>
                  <p className="text-slate-400 font-body text-sm mt-1">Complete trainings from the Active tab to see them here</p>
                </CardContent>
              </Card>
            ) : (
              completedMaterials.map((mat, i) => (
                <motion.div key={mat.id || i} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.04 }}>
                  <Card className="border-slate-200 bg-slate-50/50" data-testid={`completed-training-${mat.material_id}`}>
                    <CardContent className="p-5 flex items-start gap-4">
                      <div className="w-10 h-10 bg-emerald-50 rounded-lg flex items-center justify-center flex-shrink-0">
                        <CheckCircle className="w-5 h-5 text-emerald-500" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <h3 className="font-heading font-semibold text-navy text-sm">{mat.material_title || mat.title || "Training"}</h3>
                        {mat.description && <p className="text-xs text-slate-500 mt-1 line-clamp-2">{mat.description}</p>}
                        <div className="flex items-center gap-3 mt-2">
                          <span className="text-xs bg-emerald-100 text-emerald-700 px-2 py-0.5 rounded">Completed</span>
                          <span className="text-xs text-slate-400">
                            <Clock className="w-3 h-3 inline mr-1" />
                            {new Date(mat.completed_at).toLocaleDateString()}
                          </span>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </motion.div>
              ))
            )}
          </div>
        )}
      </div>
    </DealerLayout>
  );
}
